import { format, parseISO } from "date-fns";
import { fitArt, type ArtLayer, type CustomArt } from "../schedule-art/art-transform";
import { emptyProfile, emptySlot, type CommonOptions, type Profile, type StreamSlot } from "../schedule-document/schedule-values";
import { findImage } from "./image-registry";
import { shiftKeys, weekShiftDays } from "./shift-week";
import { getWeek, systemTimezone } from "../schedule-time/schedule-dates";
import { expandScheduleSlots } from "../schedule-document/schedule-json";
import { loadSavedSchedule } from "../schedule-document/schedule-storage";
import { schedulePresets, type SchedulePreset } from "../schedule-presets/schedule-presets";
import type { ScheduleEvent } from "./history-events";

export type ScheduleFold = {
  slots: Record<string, StreamSlot>;
  commonOptions: CommonOptions;
  offlineMessages: Record<string, string>;
  offlineActive: Record<string, boolean>;
  date: string;
  timezone: string;
  presetId: string;
  profile: Profile;
  artistHandlePosition: number[] | null;
  artistHandleScale: number;
  artistHandleRotation: number | null;
  artLayers: ArtLayer[];
};

export type ScheduleProjection = { schedule: ProjectedSchedule; layers: CustomArt[] };

export type ProjectedSchedule = {
  slots: Record<string, StreamSlot>;
  commonOptions: CommonOptions;
  offlineMessages: Record<string, string>;
  offlineActive: Record<string, boolean>;
  selectedDate: Date;
  week: Date[];
  timezone: string;
  preset: SchedulePreset;
  profile: Profile;
  artistHandlePosition: number[] | null;
  artistHandleScale: number;
  artistHandleRotation: number | null;
};

function seedFold(): ScheduleFold {
  const saved = loadSavedSchedule();
  const date = saved ? saved.selectedDate : format(new Date(), "yyyy-MM-dd");
  const monday = parseISO(date);
  return {
    slots: saved ? expandScheduleSlots(saved.slots, monday) : {},
    commonOptions: saved?.commonOptions ?? {},
    offlineMessages: saved?.offlineMessages ?? {},
    offlineActive: saved?.offlineActive ?? {},
    date,
    timezone: saved?.timezone ?? systemTimezone,
    presetId: schedulePresets[0]!.id,
    profile: emptyProfile,
    artistHandlePosition: null,
    artistHandleScale: 1,
    artistHandleRotation: null,
    artLayers: [],
  };
}

export function applyEvent(fold: ScheduleFold, event: ScheduleEvent): ScheduleFold {
  switch (event.type) {
    case "common/set":
      return { ...fold, commonOptions: { ...fold.commonOptions, ...event.patch } };
    case "slot/set": {
      const current = fold.slots[event.key] ?? emptySlot;
      const slot = { ...current, ...event.patch };
      const slots = { ...fold.slots, [event.key]: slot };
      const offlineActive = event.patch.active
        ? { ...fold.offlineActive, [event.key.slice(0, event.key.lastIndexOf("-"))]: false }
        : fold.offlineActive;
      return { ...fold, slots, offlineActive };
    }
    case "slot/reset": {
      return { ...fold, slots: { ...fold.slots, [event.key]: { ...emptySlot } } };
    }
    case "slot/swap": {
      const source = fold.slots[event.sourceKey] ?? { ...emptySlot };
      const target = fold.slots[event.targetKey] ?? { ...emptySlot };
      const slots = { ...fold.slots, [event.sourceKey]: target, [event.targetKey]: source };
      return { ...fold, slots };
    }
    case "offline/set": {
      const { message, active } = event.patch;
      const offlineMessages = message === undefined ? fold.offlineMessages : { ...fold.offlineMessages, [event.dateKey]: message };
      const offlineActive = active === undefined ? fold.offlineActive : { ...fold.offlineActive, [event.dateKey]: active };
      const slots = active
        ? Object.fromEntries(Object.entries(fold.slots).map(([key, slot]) =>
          key.startsWith(`${event.dateKey}-`) ? [key, { ...slot, active: false }] : [key, slot]))
        : fold.slots;
      return { ...fold, slots, offlineMessages, offlineActive };
    }
    case "offline/swap": {
      const source = fold.offlineMessages[event.sourceDate] ?? "";
      const target = fold.offlineMessages[event.targetDate] ?? "";
      const offlineMessages = { ...fold.offlineMessages, [event.sourceDate]: target, [event.targetDate]: source };
      const sourceActive = fold.offlineActive[event.sourceDate] ?? false;
      const targetActive = fold.offlineActive[event.targetDate] ?? false;
      const offlineActive = { ...fold.offlineActive, [event.sourceDate]: targetActive, [event.targetDate]: sourceActive };
      return { ...fold, offlineMessages, offlineActive };
    }
    case "date/set": {
      const days = weekShiftDays(fold.date, event.date);
      return {
        ...fold,
        date: event.date,
        slots: shiftKeys(fold.slots, days),
        offlineMessages: shiftKeys(fold.offlineMessages, days),
        offlineActive: shiftKeys(fold.offlineActive, days),
      };
    }
    case "timezone/set": {
      return { ...fold, timezone: event.timezone };
    }
    case "preset/select": {
      return { ...fold, presetId: event.presetId, artistHandlePosition: null, artistHandleScale: 1, artistHandleRotation: null };
    }
    case "document/load": {
      const document = event.document;
      return {
        ...fold,
        slots: expandScheduleSlots(document.slots, parseISO(document.selectedDate)),
        commonOptions: document.commonOptions,
        offlineMessages: document.offlineMessages,
        offlineActive: document.offlineActive,
        date: document.selectedDate,
        timezone: document.timezone,
      };
    }
    case "document/reset": {
      return { ...fold, slots: {}, offlineMessages: {}, offlineActive: {} };
    }
    case "profile/set": {
      return { ...fold, profile: { ...fold.profile, ...event.patch } };
    }
    case "handle/setPosition": {
      return { ...fold, artistHandlePosition: event.position };
    }
    case "handle/setScale": {
      return { ...fold, artistHandleScale: event.scale };
    }
    case "handle/setRotation": {
      return { ...fold, artistHandleRotation: event.rotation };
    }
    case "art/addImages": {
      const added = event.images.map(image => ({ ...image, visible: true, transform: null }));
      return { ...fold, artLayers: [...fold.artLayers, ...added] };
    }
    case "art/setTransform": {
      const artLayers = fold.artLayers.map(layer => layer.imageId === event.imageId
        ? { ...layer, transform: event.transform } : layer);
      return { ...fold, artLayers };
    }
    case "art/setVisibility": {
      const artLayers = fold.artLayers.map(layer => layer.imageId === event.imageId
        ? { ...layer, visible: event.visible } : layer);
      return { ...fold, artLayers };
    }
    case "art/remove": {
      const artLayers = fold.artLayers.filter(layer => layer.imageId !== event.imageId);
      return { ...fold, artLayers };
    }
    case "art/reorder": {
      const byId = new Map(fold.artLayers.map(layer => [layer.imageId, layer]));
      const ordered = [...new Set(event.imageIds)].flatMap(id => {
        const layer = byId.get(id);
        return layer ? [layer] : [];
      });
      const remaining = fold.artLayers.filter(layer => !event.imageIds.includes(layer.imageId));
      return { ...fold, artLayers: [...ordered, ...remaining] };
    }
  }
}

export const initialFold = seedFold();

export function projectSchedule(seed: ScheduleFold, events: ScheduleEvent[], cursor: number): ScheduleProjection {
  const applied = events.slice(0, cursor);
  const fold = applied.reduce(applyEvent, seed);
  return toProjection(fold);
}

let cachedDate = { key: "", selectedDate: new Date(0), week: [] as Date[] };

function deriveDate(date: string) {
  if (cachedDate.key === date) return cachedDate;
  const selectedDate = parseISO(date);
  const week = getWeek(selectedDate);
  cachedDate = { key: date, selectedDate, week };
  return cachedDate;
}

let cachedArtLayers: ArtLayer[] = [];
let cachedArtwork: CustomArt[] = [];

function deriveArtwork(layers: ArtLayer[]) {
  // History replay rebuilds layer records; preserve the projection when only schedule inputs change.
  const unchanged = layers.length === cachedArtLayers.length && layers.every((layer, index) => {
    const cached = cachedArtLayers[index];
    return cached?.imageId === layer.imageId && cached.name === layer.name
      && cached.visible === layer.visible && cached.transform === layer.transform;
  });
  if (unchanged) return cachedArtwork;
  cachedArtLayers = layers;
  cachedArtwork = layers.flatMap(layer => {
    const image = findImage(layer.imageId);
    if (!image) return [];
    const transform = layer.transform ?? fitArt(image);
    return [{ ...layer, image, transform }];
  });
  return cachedArtwork;
}

export function toProjection(fold: ScheduleFold): ScheduleProjection {
  const { selectedDate, week } = deriveDate(fold.date);
  const preset = schedulePresets.find(entry => entry.id === fold.presetId) ?? schedulePresets[0]!;
  const layers = deriveArtwork(fold.artLayers);
  const schedule: ProjectedSchedule = {
    slots: fold.slots,
    commonOptions: fold.commonOptions,
    offlineMessages: fold.offlineMessages,
    offlineActive: fold.offlineActive,
    selectedDate,
    week,
    timezone: fold.timezone,
    preset,
    profile: fold.profile,
    artistHandlePosition: fold.artistHandlePosition,
    artistHandleScale: fold.artistHandleScale,
    artistHandleRotation: fold.artistHandleRotation,
  };
  return { schedule, layers };
}
